import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { LayoutDashboard, Handshake, FileText, CalendarDays, Telescope, MessageCircle, ShoppingBag, GraduationCap, ClipboardList, Settings } from 'lucide-react';

const ROLES_CLAIM = 'https://raah-portal.com/roles';

const navItems = [
  { path: '/',             label: 'Dashboard',      Icon: LayoutDashboard },
  { path: '/engage',       label: 'Engage RAAH',    Icon: Handshake },
  { path: '/materials',    label: 'Materials',       Icon: FileText },
  { path: '/booking',      label: 'Book Time',       Icon: CalendarDays },
  { path: '/capabilities', label: 'Capabilities',    Icon: Telescope },
  { path: '/ask',          label: 'Ask a Question',  Icon: MessageCircle },
  { path: '/marketplace',  label: 'Marketplace',     Icon: ShoppingBag },
  { path: '/workshops',    label: 'Workshops',       Icon: GraduationCap },
  { path: '/my-requests',  label: 'My Requests',     Icon: ClipboardList },
];

const adminItems = [
  { path: '/admin', label: 'Admin Console', Icon: Settings },
];

// Inject styles once
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');

  .raah-sidebar {
    font-family: 'DM Sans', sans-serif;
    position: relative;
    z-index: 100;
  }

  /* ── Desktop tray ── */
  .raah-tray {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 72px;
    min-height: 100vh;
    background: #f0f0ee;
    padding: 14px 10px 20px;
    box-sizing: border-box;
    transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    border-right: 1px solid rgba(0,0,0,0.06);
    position: relative;
  }
  .raah-tray.expanded {
    width: 220px;
    align-items: flex-start;
  }
  .raah-tray.mobile {
    position: relative;
  }
  .raah-tray.mobile.expanded {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 200;
    box-shadow: 4px 0 24px rgba(0,0,0,0.12);
  }
  .raah-overlay-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.3);
    z-index: 199;
  }

  /* ── Logo area ── */
  .raah-logo-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 4px 20px;
    width: 100%;
    overflow: hidden;
  }
  .raah-logo-mark {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: #111;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    letter-spacing: -0.5px;
  }
  .raah-logo-text {
    white-space: nowrap;
    opacity: 0;
    transform: translateX(-6px);
    transition: opacity 0.2s 0.05s, transform 0.2s 0.05s;
    pointer-events: none;
  }
  .raah-tray.expanded .raah-logo-text {
    opacity: 1;
    transform: translateX(0);
  }
  .raah-logo-title {
    font-size: 15px;
    font-weight: 600;
    color: #111;
    line-height: 1.1;
  }
  .raah-logo-sub {
    font-size: 10px;
    color: #999;
    font-weight: 500;
    letter-spacing: 0.03em;
  }

  /* ── Nav items ── */
  .raah-nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    flex: 1;
  }

  .raah-nav-section-label {
    font-size: 9px;
    font-weight: 600;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    padding: 10px 6px 2px;
    white-space: nowrap;
    overflow: hidden;
    opacity: 0;
    height: 0;
    transition: opacity 0.15s, height 0.15s;
  }
  .raah-tray.expanded .raah-nav-section-label {
    opacity: 1;
    height: auto;
  }

  .raah-nav-link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0;
    border-radius: 12px;
    text-decoration: none;
    color: #555;
    transition: background 0.15s, color 0.15s;
    overflow: hidden;
    width: 100%;
  }
  .raah-nav-link:hover .raah-icon-wrap {
    background: #e2e2df;
  }
  .raah-nav-link.active .raah-icon-wrap {
    background: #111;
    color: #fff;
  }
  .raah-nav-link.active {
    color: #111;
  }
  .raah-nav-link.active .raah-icon-wrap span {
    filter: grayscale(0) brightness(1);
  }

  .raah-icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;
    background: transparent;
    color: #666;
  }

  .raah-nav-label {
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    transform: translateX(-8px);
    transition: opacity 0.18s 0.04s, transform 0.18s 0.04s;
    pointer-events: none;
  }
  .raah-tray.expanded .raah-nav-label {
    opacity: 1;
    transform: translateX(0);
  }

  /* ── Toggle button ── */
  .raah-toggle {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #e6e6e3;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #555;
    transition: background 0.15s, transform 0.25s;
    flex-shrink: 0;
    margin-top: 8px;
    align-self: center;
  }
  .raah-tray.expanded .raah-toggle {
    align-self: flex-end;
    margin-right: 0;
  }
  .raah-toggle:hover {
    background: #ddddd9;
  }
  .raah-toggle-arrow {
    display: inline-block;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .raah-tray.expanded .raah-toggle-arrow {
    transform: rotate(180deg);
  }








  /* ── Mobile drawer ── */

















  
  }
`;


export default function Sidebar() {
  const { user } = useAuth0();
  const location = useLocation();
  const roles   = user?.[ROLES_CLAIM] || [];
  const isAdmin = roles.includes('Partner Portal Admin');

  const [expanded, setExpanded]     = useState(() => window.innerWidth > 768);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile]     = useState(() => window.innerWidth <= 768);

  // Inject CSS once
  useEffect(() => {
    if (document.getElementById('raah-sidebar-css')) return;
    const el = document.createElement('style');
    el.id = 'raah-sidebar-css';
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);

  // Track viewport
  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile((prevMobile) => {
        if (prevMobile !== mobile) setExpanded(!mobile);
        return mobile;
      });
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Close overlay on navigation
  useEffect(() => {
    setDrawerOpen(false);
    if (isMobile) setExpanded(false);
  }, [location.pathname]);

  const isActive = (path) => path === '/'
    ? location.pathname === '/'
    : location.pathname.startsWith(path);

  const allDrawerItems = [
    ...navItems,
    ...(isAdmin ? adminItems : []),
  ];

  return (
    <div className="raah-sidebar">
      {/* ── Desktop tray ── */}
      {isMobile && expanded && (
        <div className="raah-overlay-backdrop" onClick={() => setExpanded(false)} />
      )}
      <aside className={`raah-tray ${expanded ? 'expanded' : ''} ${isMobile ? 'mobile' : ''}`}>
        {/* Logo */}
        {!isMobile && <div className="raah-logo-wrap">
          <div className="raah-logo-mark">R</div>
          <div className="raah-logo-text">
            <div className="raah-logo-title">RAAH</div>
            <div className="raah-logo-sub">Partner Portal</div>
          </div>
        </div>}

        {/* Nav */}
        <nav className="raah-nav">
          {!isMobile && <div className="raah-nav-section-label">Journeys</div>}
          {navItems.map(({ path, label, Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) => `raah-nav-link${isActive ? ' active' : ''}`}
            >
              <div className="raah-icon-wrap">
                <Icon size={20} strokeWidth={1.75} />
              </div>
              <span className="raah-nav-label">{label}</span>
            </NavLink>
          ))}

          {isAdmin && !isMobile && (
            <>
              <div className="raah-nav-section-label" style={{ marginTop: '8px' }}>Admin</div>
              {adminItems.map(({ path, label, Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={({ isActive }) => `raah-nav-link${isActive ? ' active' : ''}`}
                >
                  <div className="raah-icon-wrap">
                    <Icon size={20} strokeWidth={1.75} />
                  </div>
                  <span className="raah-nav-label">{label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* Toggle */}
        <button className="raah-toggle" onClick={() => setExpanded(e => !e)} title={expanded ? 'Collapse' : 'Expand'}>
          <span className="raah-toggle-arrow">›</span>
        </button>
      </aside>
    </div>
  );
}